import { stateText, type AvailabilityTone } from "@/lib/acd";
import type { CloserAsk, CloserNoteLine, LeadHandoffConversation } from "@/lib/types";

/**
 * Asking for a closer (platform ADR-187), in words: what an ask's card says to the person reading
 * it, and its clocks. Pure, so the wording is tested once for the card, the setter's strip and
 * the closer's banner, which all draw the same card.
 */

export type AskTone = "warning" | "success" | "neutral" | "accent";

export interface AskStatus {
  label: string;
  tone: AskTone;
  line: string;
}

/** Whole seconds from `now` until `until`, never below 0; 0 for no moment. */
export function secondsLeft(until: string | null, now: number): number {
  if (!until) return 0;
  const at = Date.parse(until);
  return Number.isFinite(at) ? Math.max(0, Math.ceil((at - now) / 1000)) : 0;
}

/** 1:42, 0:05. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Who it was asked of: the team, or the one person. */
export function askedOf(ask: CloserAsk): string {
  return ask.team_name ?? ask.asked_user_name ?? "a closer";
}

/** The head of an ask's card, for the person reading it at `now`. */
export function askStatus(ask: CloserAsk, now: number): AskStatus {
  const asker = ask.mine ? "You" : (ask.asked_by_name ?? "A rep");
  const taker = ask.took ? "you" : (ask.taken_by_name ?? "a closer");
  switch (ask.state) {
    case "waiting": {
      const left = clock(secondsLeft(ask.wait_until, now));
      return ask.mine
        ? { label: "Waiting for a closer", tone: "warning", line: `Sent to ${askedOf(ask)} · ${left} left` }
        : { label: "Needs a closer", tone: "warning", line: `${asker} is on the call · ${left} left to take it` };
    }
    case "taken": {
      const held = clock(secondsLeft(ask.hold_until, now));
      if (ask.took) {
        return {
          label: "You took it",
          tone: "success",
          line: `${asker} transfers the caller to you next. Other callers wait for ${held}.`,
        };
      }
      if (ask.mine) {
        return {
          label: `${ask.taken_by_name ?? "A closer"} took it`,
          tone: "success",
          line: `Waiting for your transfer for ${held}.`,
        };
      }
      return { label: `Taken by ${ask.taken_by_name ?? "a closer"}`, tone: "neutral", line: "Nothing to do." };
    }
    case "transferred":
      return {
        label: ask.took ? "Sent to you" : `Transferred to ${ask.taken_by_name ?? "a closer"}`,
        tone: "accent",
        line: ask.mine ? "You sent the caller over." : `${asker} sent the caller over.`,
      };
    case "cancelled":
      return { label: "Called off", tone: "neutral", line: `${asker} kept the call.` };
    case "expired":
      return ask.taken_by === null
        ? { label: "No one took it", tone: "neutral", line: `Nobody took it in time. ${asker} kept the call.` }
        : { label: "The transfer did not come", tone: "neutral", line: `${taker === "you" ? "You" : ask.taken_by_name} took it; the hold ran out.` };
    case "ended":
      return { label: "Call ended", tone: "neutral", line: "The caller hung up before the transfer." };
  }
}

/**
 * Where a drafted note's lines came from (platform ADR-188), in the words under the note box:
 * "From the call: DOL, Accident Details", then the contact's, the mixed ones, and the ones not
 * asked yet. Groups with no line are left out.
 */
export function draftSources(lines: readonly CloserNoteLine[]): string[] {
  const groups: [CloserNoteLine["source"], string][] = [
    ["call", "From the call"],
    ["both", "From the call and the contact"],
    ["contact", "From the contact"],
    ["none", "Not asked yet"],
  ];
  return groups
    .map(([source, words]) => {
      const labels = lines.filter((l) => l.source === source).map((l) => l.label);
      return labels.length ? `${words}: ${labels.join(", ")}` : null;
    })
    .filter((x): x is string => x !== null);
}

/**
 * An ask on the contact's record (platform ADR-189), in words: who asked whom, and how it ended.
 * "Ethan Cole asked Closers" and "Transferred to Carlos Vega". Read by anyone who may open the
 * contact, so it names people, never "you".
 */
export function handoffHeadline(h: LeadHandoffConversation): { asked: string; outcome: string } {
  const asked = `${h.asked_by ?? "A rep"} asked ${h.asked_of ?? "a closer"}`;
  const outcome = {
    waiting: "Waiting for a closer",
    taken: `${h.taken_by ?? "A closer"} took it`,
    transferred: `Transferred to ${h.taken_by ?? "a closer"}`,
    cancelled: "Called off before the transfer",
    expired: h.taken_by ? `${h.taken_by} took it; the transfer did not come` : "Nobody took it in time",
    ended: "The call ended before the transfer",
  }[h.state];
  return { asked, outcome };
}

/** A person's state on the line, as the dialog lists them: the phone's words for it, and "Not
 *  signed in here" for somebody on the line who is not taking its calls. */
export function lineStateText(state: string): { label: string; tone: AvailabilityTone } {
  if (state === "signed_out") return { label: "Not signed in here", tone: "neutral" };
  const { label, tone } = stateText(state);
  return { label, tone };
}
